import { Component, ViewChild, ElementRef, OnInit, OnDestroy, AfterViewInit, HostListener } from "@angular/core";
import { Subscription } from "rxjs";

import { Config } from "src/app/config";
import { ExamService } from "src/app/services/exam.service";
import { EventsHubService, EDITORMESSAGETYPE, EditorMessage, PAGEPROPERTY } from "src/app/services/eventshub.service";
import { ModalDialogService } from "src/app/modals/modaldialog/modaldialog.service";
import { Exam } from "src/app/models/exam";
import { PageObject, PAGEOBJECTTYPE } from "src/app/models/pageobject";
import { PageObjectContainer } from "src/app/models/pageobjectcontainer";
import { Point } from "src/app/models/point";

const enum OBJECTEDIT {
  Move,
  TopLeft,
  BottomLeft,
  TopRight,
  BottomRight,
  Top,
  Left,
  Bottom,
  Right,
  None,
}

@Component({
  selector: "hkt-editor",
  templateUrl: "./editor.component.html",
  styleUrls: ["./editor.component.scss"],
  host: {
    "(contextmenu)": "onCancelEvent($event)", // Cancel Context Menu (right click not working)
    "(dragstart)": "onCancelEvent($event)", // Cancel Context Menu (right click not working)
    "(mousedown)": "onMouseDown($event)",
    "(mouseup)": "onMouseUp($event)",
    "(mousemove)": "onMouseMove($event)",
    "(mouseleave)": "onMouseUp($event)",
    "(mousewheel)": "onMouseWheel($event)",
  },
})
export class EditorComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild("divWorkplace") divWorkplaceElementRef: ElementRef;

  exam: Exam;
  mouseLeftDown: boolean;
  mouseRightDown: boolean;
  mousePosX: number;
  mousePosY: number;
  mouseLastPosX: number;
  mouseLastPosY: number;
  transform: string;
  pageX: number;
  pageY: number;
  pageScale: number;
  pageTransform: string;
  pageClass: string;
  savedPage: number;
  lastPage: number;
  selectedObject: PageObject;
  selectedObjectEdit: OBJECTEDIT;
  private examSubscription: Subscription;
  private eventsHubSubscription: Subscription;
  private onPagePropertyChangeSubscription: Subscription;
  currentPageContainer: PageObjectContainer;
  private copyProperties: any;

  constructor(
    private examService: ExamService,
    private eventsHubService: EventsHubService,
    private modalDialogService: ModalDialogService,
  ) {
    this.mouseLeftDown = false;
    this.mouseRightDown = false;
    this.mouseLastPosX = 0;
    this.mouseLastPosY = 0;
    this.mousePosX = 0;
    this.mousePosY = 0;
    this.pageX = 0;
    this.pageY = 0;
    this.pageScale = 1;
    this.savedPage = 0;
    this.lastPage = 0;
    this.updatePageTransform();
    this.pageClass = "page";
    this.selectedObject = null;

    this.currentPageContainer = new PageObjectContainer();
    this.currentPageContainer.scrollHorizontal = false;
    this.currentPageContainer.scrollVertical = false;
  }

  ngOnInit(): void {
    this.exam = this.examService.currentExam;
    this.examSubscription = this.examService.onCurrentExamChange.subscribe(exam => {
      this.exam = exam;
      this.currentPageContainer.objects = this.exam.pages[this.exam.currentPageIndex].objects;
      this.setViewFit();
      this.updatePageClass();
    });

    this.eventsHubSubscription = this.eventsHubService.onEditorMessage.subscribe(msg => {
      if (msg.type == EDITORMESSAGETYPE.View100) {
        this.setView100();
      } else if (msg.type == EDITORMESSAGETYPE.ViewFit) {
        this.setViewFit();
      } else if (msg.type == EDITORMESSAGETYPE.SelectPage) {
        this.selectedObject = null;
        this.currentPageContainer.objects = this.exam.pages[this.exam.currentPageIndex].objects;
        this.updatePageClass();
      } else if (msg.type == EDITORMESSAGETYPE.SelectObject) {
        this.selectedObject = msg.data;
      }
    });

    this.onPagePropertyChangeSubscription = this.eventsHubService.onPagePropertyChange.subscribe(change => {
      if (change.type == PAGEPROPERTY.Background) {
        this.updatePageClass();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.examSubscription) {
      this.examSubscription.unsubscribe();
      this.examSubscription = null;
    }

    if (this.eventsHubSubscription) {
      this.eventsHubSubscription.unsubscribe();
      this.eventsHubSubscription = null;
    }

    if (this.onPagePropertyChangeSubscription) {
      this.onPagePropertyChangeSubscription.unsubscribe();
      this.onPagePropertyChangeSubscription = null;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.setViewFit(), 0);
  }

  onCancelEvent(event): boolean {
    return false;
  }

  private updatePageClass() {
    let pageClass = "page";
    if (this.exam.pages[this.exam.currentPageIndex].background != "") {
      pageClass += " " + this.exam.pages[this.exam.currentPageIndex].background;
    }
    this.pageClass = pageClass;
  }

  private updatePageTransform() {
    this.pageTransform =
      "matrix(" + this.pageScale + ", 0, 0, " + this.pageScale + ", " + this.pageX + ", " + this.pageY + ")";
  }

  private updateMousePosition(event: MouseEvent): void {
    this.mouseLastPosX = this.mousePosX;
    this.mouseLastPosY = this.mousePosY;

    this.mousePosX = event.clientX;
    this.mousePosY = event.clientY;
  }

  private getAbsoluteObjectPosition(obj: PageObject): Point {
    let point: Point = {
      x: obj.x,
      y: obj.y,
    };

    let parentContainer = this.currentPageContainer.getObjectContainer(obj);
    while (parentContainer && parentContainer != this.currentPageContainer) {
      point.x += parentContainer.x - parentContainer.containerOffsetX;
      point.y += parentContainer.y - parentContainer.containerOffsetY;
      parentContainer = this.currentPageContainer.getObjectContainer(parentContainer);
    }

    return point;
  }

  onMouseDown(event: MouseEvent): void {
    this.updateMousePosition(event);

    if (event.button == 0) {
      this.mouseLeftDown = true;

      // Check if editing
      if (this.selectedObject != null) {
        if (!this.selectedObject.lock) {
          let target: any = event.target;
          if (target) {
            if (target.className == "page-object-edit-move") {
              this.selectedObjectEdit = OBJECTEDIT.Move;

              let centerX = this.selectedObject.x + this.selectedObject.width / 2;
              let centerY = this.selectedObject.y + this.selectedObject.height / 2;
              let objCurrentContainer = this.currentPageContainer.getObjectContainer(this.selectedObject);
              let pos = this.getAbsoluteObjectPosition(this.selectedObject);
              objCurrentContainer.removeObject(this.selectedObject);
              this.currentPageContainer.addObject(this.selectedObject);
              this.selectedObject.x = pos.x;
              this.selectedObject.y = pos.y;

              this.sendMessageSelectedObjectChanged();
            } else if (target.className == "page-object-edit-topleft") {
              this.selectedObjectEdit = OBJECTEDIT.TopLeft;
            } else if (target.className == "page-object-edit-topright") {
              this.selectedObjectEdit = OBJECTEDIT.TopRight;
            } else if (target.className == "page-object-edit-bottomleft") {
              this.selectedObjectEdit = OBJECTEDIT.BottomLeft;
            } else if (target.className == "page-object-edit-bottomright") {
              this.selectedObjectEdit = OBJECTEDIT.BottomRight;
            } else if (target.className == "page-object-edit-top") {
              this.selectedObjectEdit = OBJECTEDIT.Top;
            } else if (target.className == "page-object-edit-left") {
              this.selectedObjectEdit = OBJECTEDIT.Left;
            } else if (target.className == "page-object-edit-bottom") {
              this.selectedObjectEdit = OBJECTEDIT.Bottom;
            } else if (target.className == "page-object-edit-right") {
              this.selectedObjectEdit = OBJECTEDIT.Right;
            } else {
              this.selectedObjectEdit = OBJECTEDIT.None;
            }
          }
        }
      }
    }
    if (event.button == 2) {
      this.mouseRightDown = true;
    }
  }

  onMouseUp(event: MouseEvent): void {
    this.updateMousePosition(event);
    this.currentPageContainer.clearContainerHover();

    if (this.mouseLeftDown) {
      if (this.selectedObject != null && !this.selectedObject.lock) {
        if (this.selectedObjectEdit == OBJECTEDIT.Move) {
          this.selectedObject.x += (this.mousePosX - this.mouseLastPosX) / this.pageScale;
          this.selectedObject.y += (this.mousePosY - this.mouseLastPosY) / this.pageScale;

          let centerX = this.selectedObject.x + this.selectedObject.width / 2;
          let centerY = this.selectedObject.y + this.selectedObject.height / 2;
          let container = this.currentPageContainer.getContainerAtPos(centerX, centerY);
          if (container == this.selectedObject) {
            container = this.currentPageContainer.getObjectContainer(container);
          }
          let objCurrentContainer = this.currentPageContainer.getObjectContainer(this.selectedObject);
          objCurrentContainer.removeObject(this.selectedObject);

          let pos = this.getAbsoluteObjectPosition(container);
          this.selectedObject.x -= pos.x - container.containerOffsetX;
          this.selectedObject.y -= pos.y - container.containerOffsetY;
          container.addObject(this.selectedObject);

          this.sendMessageSelectedObjectChanged();
        }
      }
    }

    if (event.type == "mouseleave") {
      this.mouseLeftDown = false;
      this.mouseRightDown = false;
      return;
    }

    if (event.button == 0) {
      this.mouseLeftDown = false;
    }
    if (event.button == 2) {
      this.mouseRightDown = false;
    }
  }

  onMouseMove(event: MouseEvent): void {
    this.updateMousePosition(event);

    if (this.mouseLeftDown) {
      if (this.selectedObject != null && !this.selectedObject.lock) {
        if (this.selectedObjectEdit == OBJECTEDIT.Move) {
          this.selectedObject.x += (this.mousePosX - this.mouseLastPosX) / this.pageScale;
          this.selectedObject.y += (this.mousePosY - this.mouseLastPosY) / this.pageScale;

          let centerX = this.selectedObject.x + this.selectedObject.width / 2;
          let centerY = this.selectedObject.y + this.selectedObject.height / 2;
          let container = this.currentPageContainer.getContainerAtPos(centerX, centerY);
          if (container == this.selectedObject) {
            container = this.currentPageContainer.getObjectContainer(container);
          }
          this.currentPageContainer.clearContainerHover();
          if (container != this.currentPageContainer) {
            container.containerHover = true;
          }
        } else if (this.selectedObjectEdit == OBJECTEDIT.TopLeft) {
          let right = this.selectedObject.x + this.selectedObject.width;
          let bottom = this.selectedObject.y + this.selectedObject.height;
          this.selectedObject.x += (this.mousePosX - this.mouseLastPosX) / this.pageScale;
          this.selectedObject.width = right - this.selectedObject.x;
          this.selectedObject.y += (this.mousePosY - this.mouseLastPosY) / this.pageScale;
          this.selectedObject.height = bottom - this.selectedObject.y;
        } else if (this.selectedObjectEdit == OBJECTEDIT.TopRight) {
          let bottom = this.selectedObject.y + this.selectedObject.height;
          this.selectedObject.y += (this.mousePosY - this.mouseLastPosY) / this.pageScale;
          this.selectedObject.width += (this.mousePosX - this.mouseLastPosX) / this.pageScale;
          this.selectedObject.height = bottom - this.selectedObject.y;
        } else if (this.selectedObjectEdit == OBJECTEDIT.BottomLeft) {
          let right = this.selectedObject.x + this.selectedObject.width;
          this.selectedObject.x += (this.mousePosX - this.mouseLastPosX) / this.pageScale;
          this.selectedObject.width = right - this.selectedObject.x;
          this.selectedObject.height += (this.mousePosY - this.mouseLastPosY) / this.pageScale;
        } else if (this.selectedObjectEdit == OBJECTEDIT.BottomRight) {
          this.selectedObject.width += (this.mousePosX - this.mouseLastPosX) / this.pageScale;
          this.selectedObject.height += (this.mousePosY - this.mouseLastPosY) / this.pageScale;
        } else if (this.selectedObjectEdit == OBJECTEDIT.Top) {
          let bottom = this.selectedObject.y + this.selectedObject.height;
          this.selectedObject.y += (this.mousePosY - this.mouseLastPosY) / this.pageScale;
          this.selectedObject.height = bottom - this.selectedObject.y;
        } else if (this.selectedObjectEdit == OBJECTEDIT.Right) {
          this.selectedObject.width += (this.mousePosX - this.mouseLastPosX) / this.pageScale;
        } else if (this.selectedObjectEdit == OBJECTEDIT.Bottom) {
          this.selectedObject.height += (this.mousePosY - this.mouseLastPosY) / this.pageScale;
        } else if (this.selectedObjectEdit == OBJECTEDIT.Left) {
          let right = this.selectedObject.x + this.selectedObject.width;
          this.selectedObject.x += (this.mousePosX - this.mouseLastPosX) / this.pageScale;
          this.selectedObject.width = right - this.selectedObject.x;
        }

        this.selectedObject.width = this.selectedObject.width < 10 ? 10 : this.selectedObject.width;
        this.selectedObject.height = this.selectedObject.height < 10 ? 10 : this.selectedObject.height;
      }
    }
    if (this.mouseRightDown) {
      this.pageX += this.mousePosX - this.mouseLastPosX;
      this.pageY += this.mousePosY - this.mouseLastPosY;
      this.updatePageTransform();
    }
  }

  onMouseWheel(event: WheelEvent): void {
    this.updateMousePosition(event);
    let oldPageScale = this.pageScale;

    if (event.deltaY > 0) {
      this.pageScale /= 1.1;
    } else if (event.deltaY < 0) {
      this.pageScale *= 1.1;
    }

    if (oldPageScale != this.pageScale) {
      let mouseX = event.layerX - (this.exam.pageWidth * (1 - oldPageScale)) / 2;
      let mouseY = event.layerY - (this.exam.pageHeight * (1 - oldPageScale)) / 2;

      let x = mouseX - this.pageX;
      let y = mouseY - this.pageY;
      let relativeX = x - this.exam.pageWidth / 2;
      let relativeY = y - this.exam.pageHeight / 2;
      this.pageX = relativeX - ((relativeX - this.pageX) * this.pageScale) / oldPageScale;
      this.pageY = relativeY - ((relativeY - this.pageY) * this.pageScale) / oldPageScale;
    }
    this.updatePageTransform();
  }

  onKeyUp(event: KeyboardEvent): void {
    if (event.ctrlKey) {
      if (event.key == "ArrowRight") {
        if (this.exam.currentPageIndex >= this.exam.pages.length - 1) {
          return;
        }
        this.exam.currentPageIndex++;

        let msg = new EditorMessage();
        msg.type = EDITORMESSAGETYPE.SelectPage;
        msg.data = this.exam.pages[this.exam.currentPageIndex];
        this.eventsHubService.setEditorMessage(msg);
        return;
      } else if (event.key == "ArrowLeft") {
        if (this.exam.currentPageIndex == 0) {
          return;
        }
        this.exam.currentPageIndex--;

        let msg = new EditorMessage();
        msg.type = EDITORMESSAGETYPE.SelectPage;
        msg.data = this.exam.pages[this.exam.currentPageIndex];
        this.eventsHubService.setEditorMessage(msg);
        return;
      }
    }

    if (event.key == "a") {
      if (event.ctrlKey) {
        this.savedPage = this.exam.currentPageIndex;
        return;
      } else {
        this.lastPage = this.exam.currentPageIndex;
        this.exam.currentPageIndex = this.savedPage;

        let msg = new EditorMessage();
        msg.type = EDITORMESSAGETYPE.SelectPage;
        msg.data = this.exam.pages[this.exam.currentPageIndex];
        this.eventsHubService.setEditorMessage(msg);
        return;
      }
    } else if (event.key == "s") {
      this.exam.currentPageIndex = this.lastPage;

      let msg = new EditorMessage();
      msg.type = EDITORMESSAGETYPE.SelectPage;
      msg.data = this.exam.pages[this.exam.currentPageIndex];
      this.eventsHubService.setEditorMessage(msg);
      return;
    }

    if (this.selectedObject != null) {
      if (event.key == "l") {
        this.selectedObject.lock = !this.selectedObject.lock;
        return;
      }
      if (event.key == "m") {
        this.selectedObject.x = Math.floor((this.exam.pageWidth - this.selectedObject.width) / 2);
        return;
      }
      if (!this.selectedObject.lock) {
        if (event.key == "ArrowRight") {
          if (event.shiftKey) {
            this.selectedObject.x += 5;
          } else {
            this.selectedObject.x++;
          }
          return;
        } else if (event.key == "ArrowLeft") {
          if (event.shiftKey) {
            this.selectedObject.x -= 5;
          } else {
            this.selectedObject.x--;
          }
          return;
        }
        if (event.key == "ArrowDown") {
          if (event.shiftKey) {
            this.selectedObject.y += 5;
          } else {
            this.selectedObject.y++;
          }
          return;
        } else if (event.key == "ArrowUp") {
          if (event.shiftKey) {
            this.selectedObject.y -= 5;
          } else {
            this.selectedObject.y--;
          }
          return;
        }
      }

      if (event.key == "Delete") {
        let index = this.exam.pages[this.exam.currentPageIndex].objects.indexOf(this.selectedObject);
        if (index >= 0) {
          this.modalDialogService.showConfirmation(Config.APP_NAME, "Are you sure to delete the object?", () => {
            // Yes
            this.exam.pages[this.exam.currentPageIndex].objects.splice(index, 1);
            this.selectedObject = null;
            this.sendMessageSelectedObjectChanged();
          });
        }
        return;
      }

      // Copy properties
      if (event.ctrlKey && event.shiftKey && event.key == "C") {
        if (this.selectedObject.type != PAGEOBJECTTYPE.Container) {
          this.copyProperties = {};
          for (let prop in this.selectedObject) {
            if (this.selectedObject.hasOwnProperty(prop)) {
              this.copyProperties[prop] = this.selectedObject[prop];
            }
          }
        }
      }
      // Paste Properties
      if (event.ctrlKey && event.shiftKey && event.key == "V") {
        if (this.selectedObject.type != PAGEOBJECTTYPE.Container && this.copyProperties) {
          if (this.copyProperties._type != this.selectedObject.type) {
            this.selectedObject.x = this.copyProperties.x;
            this.selectedObject.y = this.copyProperties.y;
            this.selectedObject.z = this.copyProperties.z;
            this.selectedObject.width = this.copyProperties.width;
            this.selectedObject.height = this.copyProperties.height;
            this.selectedObject.lock = this.copyProperties.lock;
            this.selectedObject.visible = this.copyProperties.visible;
          } else {
            for (let prop in this.copyProperties) {
              if (
                prop == "_type" ||
                prop == "name" ||
                prop == "text" ||
                prop == "action" ||
                prop == "evaluation" ||
                prop == "evaluationResult" ||
                prop == "image" ||
                prop == "items" ||
                prop == "sound" ||
                prop == "video"
              ) {
                continue;
              }
              if (this.selectedObject.hasOwnProperty(prop)) {
                this.selectedObject[prop] = this.copyProperties[prop];
              }
            }
          }
        }
      }
    }
  }

  onSelectObject(event: MouseEvent, obj: PageObject) {
    this.selectedObject = obj;

    this.sendMessageSelectedObjectChanged();
    event.stopPropagation();
  }

  private sendMessageSelectedObjectChanged() {
    let msg = new EditorMessage();
    msg.type = EDITORMESSAGETYPE.SelectObject;
    msg.data = this.selectedObject;
    this.eventsHubService.setEditorMessage(msg);
  }

  private setView100(): void {
    let divWorkplace = this.divWorkplaceElementRef.nativeElement;

    this.pageScale = 1;
    this.pageX = (divWorkplace.offsetWidth - this.exam.pageWidth) / 2;
    this.pageY = (divWorkplace.offsetHeight - this.exam.pageHeight) / 2;
    this.updatePageTransform();
  }
  private setViewFit(): void {
    let divWorkplace = this.divWorkplaceElementRef.nativeElement;
    const margin = 10;

    let scaleX = (divWorkplace.offsetWidth - 2 * margin) / this.exam.pageWidth;
    let scaleY = (divWorkplace.offsetHeight - 2 * margin) / this.exam.pageHeight;
    this.pageScale = scaleX < scaleY ? scaleX : scaleY;
    this.pageX = (divWorkplace.offsetWidth - this.exam.pageWidth) / 2;
    this.pageY = (divWorkplace.offsetHeight - this.exam.pageHeight) / 2;
    this.updatePageTransform();
  }
}
